import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { take } from 'rxjs';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { ProductService } from '../../../../services/product/product.service';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { UgcProductResponseDto } from '../../../../common/general-types/products.dto';
import { BrandProductsCreateComponent } from '../../../my-brand/molecules/brand-products-create/brand-products-create.component';
import { BbSegmentedOption } from '../../ui/bb-segmented/bb-segmented';
import { ImageService } from '../../../../services/image/image.service';
import { BbField } from '../../ui/bb-field/bb-field';
import { BbOptionCard } from '../../ui/bb-option-card/bb-option-card';
import { DELIVERY_OPTIONS, DeliveryOption } from '../../brief-builder.constants';
import { DeliveryTypeEnum } from '@/app/common/general-types/briefs.dto';

/** Product selection: choose an existing catalogue product or create a new one. */
@Component({
  selector: 'bb-product-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BrandProductsCreateComponent, BbField, BbOptionCard],
  templateUrl: './product-section.html',
  styleUrls: ['./product-section.scss'],
})
export class ProductSection implements OnInit {

  readonly modeOptions: BbSegmentedOption[] = [
    { value: 'existing', label: 'Choose existing' },
    { value: 'new', label: 'Add new product' },
  ];

  readonly deliveryOptions: DeliveryOption[] = DELIVERY_OPTIONS;


  readonly mode = signal<'existing' | 'new'>('existing');
  readonly products = signal<UgcProductResponseDto[]>([]);
  readonly loading = signal<boolean>(true);

  /** Services */
  readonly service = inject(CreateBriefService);
  private readonly productService = inject(ProductService);
  private readonly currentUserService = inject(CurrentUserService);
  public imageService = inject(ImageService);
  /** Services - END */

  ngOnInit(): void {
    const brandId = this.currentUserService.focussedBrand?.id;
    if (!brandId) {
      this.loading.set(false);
      return;
    }
    this.productService
      .getbusinessProducts(brandId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.products.set((response as UgcProductResponseDto[]) ?? []);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  selectProduct(product: UgcProductResponseDto): void {
    this.service.selectedProduct.set(product);
  }

  clearSelection(): void {
    this.service.selectedProduct.set(undefined);
  }

  onProductAdded(product: UgcProductResponseDto): void {
    this.products.update((list) => [product, ...list]);
    this.service.selectedProduct.set(product);
    this.mode.set('existing');
  }

  deliveryIcon(id: DeliveryTypeEnum): string {
    switch (id) {
      case DeliveryTypeEnum.Discount_Code:
        return this.ticketIcon;
      case DeliveryTypeEnum.Address_Required:
        return this.packageIcon;
      default:
        return this.slashIcon;
    }
  }

  readonly videoIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>`;
  readonly shareIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" x2="12" y1="2" y2="15"/></svg>`;
  readonly infoIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>`;
  readonly slashIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m4.9 4.9 14.2 14.2"/></svg>`;
  readonly ticketIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/><path d="M13 5v2"/><path d="M13 17v2"/><path d="M13 11v2"/></svg>`;
  readonly packageIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/></svg>`;
}
